<template>
  <div class="flex flex-col p-2">
    <div class="w-full">
      <div class="flex items-center justify-between py-3">
        <div class="grid grid-cols-2 gap-3 flex-1 mr-1">
          <input type="text"
            v-model="search"
            placeholder="Search by Name"
            required
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            @keyup.enter="searchAccounts"/>

          <select v-model="status" class="w-full px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
            <option :value="null">--Filter by Status--</option>
            <option value="ALL">All</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>
        <button class="btn-sm-primary" @click="openForm">
          <i class="fa fa-plus mr-1"></i> Add Account
        </button>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <table class="min-w-full leading-normal">
            <thead>
            <tr class="border-b border-t border-gray-200">
              <th>Name</th>
              <th>ID Number</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Nationality</th>
              <th>Date Created</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
            </thead>

            <tbody>
            <template v-if="loading">
              <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
                <td>
                  <div class="h-3 bg-gray-200 rounded w-full"></div>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr :class="[(idx%2) ? rows.even : rows.odd]" v-for="(account, idx) in accounts" :key="idx">
                <td class="font-bold text-uppercase">
                  <span>{{ account.givenName }}</span>
                  <span class="mx-2">{{ account.surName }}</span>
                  <span class="mx-2" v-if="account.middleName !== 'null'">{{ account.middleName }}</span>
                </td>
                <td>
                  <span>{{ account.idNumber }}</span><br />
                  <small class="text-green">({{ cleanUnderscoreString(account.idType) }})</small>
                </td>
                <td>
                  <span>
                    <i class="fa fa-check-circle text-green mx-1" v-if="account.emailVerified"></i>
                    <i class="fa fa-times text-orange mx-1" v-else></i>
                    {{ account.email }}
                  </span>
                </td>
                <td>
                  <span>
                    <i class="fa fa-check-circle text-green mx-1" v-if="account.phoneVerified"></i>
                    <i class="fa fa-times text-orange mx-1" v-else></i>
                    {{ account.phoneNumber }}
                  </span>
                </td>
                <td>
                  <span>{{ account.nationality }}</span>
                </td>
                <td><span>{{ formatDate(account.createdAt) }}</span></td>
                <td class="font-bold">
                  <span class="text-green" v-if="account.enabled">Active</span>
                  <span class="text-red" v-else>Inactive</span>
                </td>
                <td>
                  <div class="flex gap-1 items-center">
                    <button class="btn-sm-danger"
                            v-if="account.enabled && canViewMenuItem('can_update_account_status')"
                            title="Click to disable account">
                      <i class="fa fa-times mr-1"></i> Disable
                    </button>
                    <button class="btn-sm-primary"
                            v-else-if="canViewMenuItem('can_update_account_status')"
                            title="Click to activate account">
                      <i class="fa fa-check mr-1"></i> Enable
                    </button>
                    <button class="btn-sm-primary"
                            v-if="!account.enabled && canViewMenuItem('can_resend_account_emails')"
                            title="Click to resend activation link"
                            @click="sendActivationLink(account)">
                      <i class="fa fa-reply mr-1"></i> Resend
                    </button>
                  </div>
                </td>
              </tr>
              <tr>
                <td v-if="accounts.length === 0" colspan="8" class="text-center py-6 text-sm text-gray-400">
                  No results found
                </td>
              </tr>
            </template>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="pagination.totalPages > 1"
           class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2">
        <p class="text-xs text-gray-500">Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
          <span class="font-semibold text-gray-700">{{ pagination.totalPages }}</span></p>
        <div class="flex gap-2">
          <button class="btn-sm-primary" @click="previous" :disabled="currentPage === 1">
            <i class="fa fa-chevron-left mr-1"></i> Prev
          </button>
          <button class="btn-sm-primary" @click="next" :disabled="currentPage === pagination.totalPages">
            Next <i class="fa fa-chevron-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
  <AppModal v-model="showAddAccountForm" size="lg" title="Add Account">
    <div class="col-span-11 mx-auto">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Name of MDA" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Official Email Address" />
        </div>
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Official Phone Number" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="NIN" />
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Email Address" />
        </div>
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Contact Person Name" />
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Phone Number" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4 my-2">
        <div class="col-span-12 mx-auto border border-gray rounded">
          <label class="text-sm">Letter Attachment (MOU)</label>
          <input type="file" class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-10"></div>
        <div class="col-span-2">
          <button class="btn-primary w-full bg-primary text-white px-2 py-1 rounded-sm" @click="save">
            <i class="fa fa-save mx-1"></i> Save
          </button>
        </div>
      </div>
    </div>
  </AppModal>
  <AppModal v-model="showEditAccountForm" size="lg" title="Edit Account">
    <div class="col-span-11 mx-auto">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Name of MDA" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Official Email Address" />
        </div>
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Official Phone Number" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="NIN" />
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Email Address" />
        </div>
        <div class="col-span-12">
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Contact Person Name" />
          <input type="text" class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent my-2" placeholder="Phone Number" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4 my-2">
        <div class="col-span-12 mx-auto border border-gray rounded">
          <label class="text-sm">Letter Attachment (MOU)</label>
          <input type="file" class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
      </div>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-10"></div>
        <div class="col-span-2">
          <button class="btn-primary w-full bg-primary text-white px-2 py-1 rounded-sm" @click="save">
            <i class="fa fa-save mx-1"></i> Save
          </button>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useAbility } from "@casl/vue";
import { useAccountStore } from "../stores";
import AppModal from "@/components/ui/AppModal.vue";
import { cleanUnderscoreString } from "@/commons/composables/transformations.ts";
import { formatDate, objectToQueryParams } from "@/commons/composables/utilities.ts";
import { useNotificationsStore } from "@/app/notify/notifications.ts";
import { UserAccount } from "../types";

interface Account {
    id: string
    givenName: string
    surName: string
    middleName: string
    idNumber: string
    idType: string
    email: string
    phoneNumber: string
    nationality: string
    createdAt: string
    enabled: boolean
    emailVerified: boolean
    phoneVerified: boolean
}

interface Pagination {
    totalElements: number
    totalPages: number
}

const accountStore = useAccountStore();
const notify = useNotificationsStore()

const showAddAccountForm = ref(false)
const showEditAccountForm = ref(false)
const loading = ref(false)
const currentPage = ref(0)
const limit = ref(10)
const accounts = ref<UserAccount[]>([]);
const pagination = ref<Pagination>({
    totalElements: 0,
    totalPages: 0
})
const rows = ref({
    odd: "border-t border-gray-200 hover:bg-gray-10",
    even: "border-t border-gray-200 bg-gray-10"
})
const search = ref("")
const status = ref<string | null>(null)

onMounted(() => {
    fetch()
})

const fetch = () => {
    loading.value = true
    let filters: any = { "accountType": "PARTNER" }

    if (status.value) {
        filters.accountStatus = status.value
    }

  accountStore.fetchUsers(objectToQueryParams(filters));
}

const next = () => {
    if (currentPage.value < pagination.value.totalPages) {
        currentPage.value++
        fetch()
    }
}

const searchAccounts = () => {
    // TODO: Implement search functionality
}

const save = () => {
    // TODO: Implement save functionality
}

const sendActivationLink = (account: UserAccount) => {
    console.log(account)
}

const openForm = () => {
    showAddAccountForm.value = true
}

const previous = () => {
    if (currentPage.value > 1) {
        currentPage.value--
        fetch()
    } else {
        notify.warning("No more data to show")
    }
}

const { can } = useAbility();
const canViewMenuItem = (permission: string) => can(permission, "general") === true;

// Watchers
watch(() => accountStore.officer_accounts, (data) => {
    if (data) {
        loading.value = false
        accounts.value = data
    }
})
</script>
